import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// ─── Scroll-fade hook ──────────────────────────────────────────────────────────
function useFadeIn(delay = 0) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = setTimeout(() => setVisible(true), delay);
          observer.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
    );
    observer.observe(el);
    return () => { observer.unobserve(el); clearTimeout(timer); };
  }, [delay]);
  return [ref, visible];
}

// ─── Icons ─────────────────────────────────────────────────────────────────────
const S = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function IconMonitor({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...S}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}
function IconShield({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...S}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}
function IconLayers({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...S}>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}
function IconZap({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...S}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0 mt-0.5" {...S}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function IconArrow() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" {...S}>
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}
function IconCheckCircle() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" {...S}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

// ─── Tech Ticker ───────────────────────────────────────────────────────────────
const TICKER_BASE_VEL = -0.0275;

function TechTicker({ items }) {
  const trackRef = useRef(null);
  const stateRef = useRef({
    offset:   0,
    velocity: TICKER_BASE_VEL,
    setWidth: 0,
    dragging: false,
    lastX:    0,
    lastY:    0,
    lastTime: 0,
  });
  const [offset,  setOffset]  = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (trackRef.current) {
      const w = trackRef.current.scrollWidth / 3;
      stateRef.current.setWidth = w;
      stateRef.current.offset   = -w;
      setOffset(-w);
    }
  }, []);

  useEffect(() => {
    let last = performance.now();
    let raf;
    const tick = (now) => {
      const dt = Math.min(now - last, 50);
      last = now;
      const s = stateRef.current;

      if (!s.dragging) {
        s.offset   += s.velocity * dt;
        s.velocity += (TICKER_BASE_VEL - s.velocity) * 0.028;
      }

      const W = s.setWidth;
      if (W > 0) {
        if (s.offset < -W * 2) s.offset += W;
        if (s.offset > 0)      s.offset -= W;
      }

      setOffset(s.offset);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      const s = stateRef.current;
      if (!s.dragging) return;
      const now   = performance.now();
      const dt    = now - s.lastTime || 1;
      const delta = e.clientX - s.lastX;
      s.offset   += delta;
      s.velocity  = delta / dt;
      s.lastX     = e.clientX;
      s.lastTime  = now;
    };
    const onUp = () => { stateRef.current.dragging = false; setDragging(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, []);

  const startDrag = (clientX, clientY = 0) => {
    const s    = stateRef.current;
    s.dragging = true;
    s.lastX    = clientX;
    s.lastY    = clientY;
    s.lastTime = performance.now();
    s.velocity = 0;
    setDragging(true);
  };

  const Dot = () => (
    <span className="mx-8 inline-block w-1 h-1 rounded-full bg-slate-300 flex-shrink-0 align-middle" />
  );

  return (
    <div
      className="relative overflow-hidden py-3"
      style={{ cursor: dragging ? 'grabbing' : 'grab' }}
      onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX); }}
      onTouchStart={(e) => startDrag(e.touches[0].clientX, e.touches[0].clientY)}
      onTouchMove={(e) => {
        const s   = stateRef.current;
        if (!s.dragging) return;
        const touch  = e.touches[0];
        const dx     = Math.abs(touch.clientX - s.lastX);
        const dy     = Math.abs(touch.clientY - s.lastY);
        if (dx > dy) e.preventDefault();
        const now    = performance.now();
        const dt     = now - s.lastTime || 1;
        const delta  = touch.clientX - s.lastX;
        s.offset    += delta;
        s.velocity   = delta / dt;
        s.lastX      = touch.clientX;
        s.lastY      = touch.clientY;
        s.lastTime   = now;
      }}
      onTouchEnd={() => { stateRef.current.dragging = false; setDragging(false); }}
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />
      <div
        ref={trackRef}
        className="flex items-center w-max select-none"
        style={{ transform: `translateX(${offset}px)`, willChange: 'transform' }}
      >
        {[0, 1, 2].map((copy) => (
          <span key={copy} className="inline-flex items-center">
            {items.map((item, i) => (
              <span key={i} className="inline-flex items-center">
                <span className="text-[13px] font-medium text-slate-500 whitespace-nowrap">{item}</span>
                <Dot />
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Data ──────────────────────────────────────────────────────────────────────
const HERO_IMAGES = ['/project1.webp', '/project2.webp', '/project3.webp'];


const SERVICES = [
  {
    title: 'Web & Mobile Development',
    Icon: IconMonitor,
    desc: 'Full-stack web applications and mobile apps built end-to-end — from architecture and API design to a polished product shipped to the App Store.',
  },
  {
    title: 'Security Consulting',
    Icon: IconShield,
    desc: 'Practical security for teams and founders — credential hygiene, access control, secure coding habits, and avoiding the mistakes that cause most breaches.',
  },
  {
    title: 'Technical Consulting',
    Icon: IconLayers,
    desc: 'Strategic guidance on architecture decisions, tech stack selection, and how to scale your product without accumulating debt.',
  },
  {
    title: 'Automation & Integrations',
    Icon: IconZap,
    desc: 'Custom APIs, webhook workflows, and scripts that connect your tools, eliminate manual work, and reduce human error.',
  },
];

const PROCESS = [
  {
    step: '01',
    title: 'Define',
    desc: 'We start with scope, not code. A clear brief means no surprises — on timeline, budget, or deliverables.',
  },
  {
    step: '02',
    title: 'Build',
    desc: 'Direct access to the engineers building your product. No hand-offs, no middlemen, no guessing.',
  },
  {
    step: '03',
    title: 'Ship',
    desc: 'Tested, documented, and deployed. We stay available after launch — not just through it.',
  },
];

const TECH = ['React', 'React Native', '.NET / C#', 'Python', 'PostgreSQL', 'Node.js', 'Security+'];

const SLIDE_INTERVAL = 8000;

// ─── Component ────────────────────────────────────────────────────────────────
export default function PeronyxHome() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError]               = useState(null);
  const [activeHeroBg, setActiveHeroBg] = useState(0);
  const [menuOpen, setMenuOpen]         = useState(false);
  const [scrolled, setScrolled]         = useState(false);
  const [service, setService]           = useState('');

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 40);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  useEffect(() => {
    const id = setInterval(
      () => setActiveHeroBg((p) => (p + 1) % HERO_IMAGES.length),
      SLIDE_INTERVAL,
    );
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!showThankYou) return;
    const id = setTimeout(() => setShowThankYou(false), 4000);
    return () => clearTimeout(id);
  }, [showThankYou]);

  const [techRef,    techVisible]    = useFadeIn(0);
  const [svcRef,     svcVisible]     = useFadeIn(0);
  const [procRef,    procVisible]    = useFadeIn(0);
  const [aboutRef,   aboutVisible]   = useFadeIn(0);
  const [contactRef, contactVisible] = useFadeIn(0);

  const fc = (v) =>
    `transition-all duration-700 ease-out ${v ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const form = e.target;
    const fd   = new FormData(form);
    fd.append('access_key', process.env.REACT_APP_WEB3FORMS_KEY);
    try {
      const res = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.success) { form.reset(); setService(''); setShowThankYou(true); }
      else setError('Something went wrong. Please try again.');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white text-slate-900 selection:bg-emerald-200/60">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ease-in-out ${
          scrolled
            ? 'bg-white/75 border-b border-black/[0.07] shadow-sm shadow-black/[0.04]'
            : 'bg-transparent border-b border-white/[0.06]'
        }`}
        style={{
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        }}
      >
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <svg
              viewBox="0 0 512 512"
              className={`h-10 w-10 group-hover:scale-105 transition-all duration-500 ${
                scrolled ? 'text-slate-900' : 'text-white'
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={38}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <g transform="translate(256,256)">
                <circle cx="0" cy="0" r="140" />
                <path d="M -50 105 L -50 -105" />
                <path d="M -50 -105 L 10 -105 A 60 60 0 0 1 10 15 L -50 15" />
              </g>
            </svg>
            <span className={`text-lg font-semibold tracking-tight transition-colors duration-500 ${
              scrolled ? 'text-slate-900' : 'text-white'
            }`}>
              Peronyx
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-[13px] font-medium">
            {['About', 'Services'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className={`tracking-tight transition-colors duration-500 ${
                  scrolled ? 'text-slate-500 hover:text-slate-900' : 'text-white/65 hover:text-white'
                }`}
              >
                {item}
              </a>
            ))}
            <a
              href="#contact"
              className={`rounded-lg px-4 py-1.5 text-[13px] tracking-tight transition-all duration-500 ${
                scrolled
                  ? 'border border-slate-300 text-slate-900 hover:bg-slate-50 hover:border-slate-400'
                  : 'border border-white/22 text-white hover:bg-white/10'
              }`}
            >
              Contact
            </a>
          </nav>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden p-2 -mr-2 rounded-lg transition-colors ${
              scrolled ? 'hover:bg-slate-100' : 'hover:bg-white/10'
            }`}
            onClick={() => setMenuOpen((p) => !p)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <svg
              viewBox="0 0 24 24"
              className={`w-5 h-5 transition-colors duration-500 ${scrolled ? 'text-slate-900' : 'text-white'}`}
              fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"
            >
              {menuOpen
                ? <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>
                : <><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></>
              }
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        <div className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${menuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
          <nav
            className={`border-t px-6 py-5 flex flex-col gap-4 text-sm font-medium ${
              scrolled ? 'border-slate-200 bg-white/90' : 'border-white/10 bg-slate-950/80'
            }`}
            style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
          >
            {['About', 'Services', 'Contact'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                onClick={() => setMenuOpen(false)}
                className={`transition-colors ${
                  scrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/80 hover:text-white'
                }`}
              >
                {item}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section id="home" className="relative min-h-[82vh] flex items-center overflow-hidden bg-slate-950">
        {HERO_IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            role="presentation"
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[2500ms] ease-out ${
              i === activeHeroBg ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="absolute inset-0 hero-color-drift" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/65 via-slate-950/20 to-transparent" />
        <div className="hero-blob absolute -top-32 left-1/2 h-96 w-[44rem] rounded-full blur-3xl bg-gradient-to-tr from-emerald-500 to-sky-400 pointer-events-none" />

        <div className="relative z-10 mx-auto max-w-6xl px-6 py-24 w-full">
          <p className="hero-fade-up text-xs font-semibold tracking-[0.28em] text-emerald-400 uppercase" style={{ animationDelay: '60ms' }}>
            Design · Build · Secure
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.04] text-white">
            <span className="inline-flex flex-wrap gap-x-[0.3em]">
              {['We', 'build', 'products'].map((word, i) => (
                <span key={word} className="word-rise" style={{ animationDelay: `${160 + i * 120}ms` }}>
                  {word}
                </span>
              ))}
            </span>
            <span className="word-rise-breathe block mt-1">
              <span className="shimmer-text">&amp; secure them.</span>
            </span>
          </h1>
          <p className="hero-fade-up mt-6 max-w-lg text-slate-300 text-lg leading-relaxed" style={{ animationDelay: '760ms' }}>
            Full-stack web apps, iOS mobile apps, and security consulting for teams that take quality seriously.
          </p>
          <p className="hero-fade-up mt-1.5 text-slate-500 text-base" style={{ animationDelay: '860ms' }}>
            Clear scope. Fair pricing. Zero jargon.
          </p>
          <div className="hero-fade-up mt-10 flex flex-wrap gap-3" style={{ animationDelay: '1000ms' }}>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-6 py-3 text-white text-sm font-semibold hover:bg-emerald-600 active:scale-[0.97] transition-all duration-150 shadow-lg shadow-emerald-900/30"
            >
              Start a Project
              <span className="inline-flex items-center"><IconArrow /></span>
            </a>
            <a
              href="#services"
              className="inline-flex items-center rounded-xl border border-white/20 px-6 py-3 text-white text-sm font-medium hover:bg-white/10 active:scale-[0.97] transition-all duration-150"
            >
              View Services
            </a>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveHeroBg(i)}
              aria-label={`Show slide ${i + 1}`}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === activeHeroBg
                  ? 'w-8 bg-emerald-400'
                  : 'w-2 bg-white/25 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* ── Tech ticker ─────────────────────────────────────────────────────── */}
      <div ref={techRef} className={`border-b border-slate-100 bg-white py-5 ${fc(techVisible)}`}>
        <p className="text-center text-[11px] font-semibold tracking-[0.2em] text-slate-400 uppercase mb-3">Built with</p>
        <TechTicker items={TECH} />
      </div>

      {/* ── Services ───────────────────────────────────────────────────────── */}
      <section id="services" className="py-28 bg-[#f5f5f7]">
        <div className="mx-auto max-w-6xl px-6">
          <div ref={svcRef} className={fc(svcVisible)}>
            <div className="flex items-end justify-between gap-4 mb-14">
              <div>
                <p className="text-xs font-semibold tracking-[0.2em] text-emerald-600 uppercase mb-3">What we do</p>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Services</h2>
              </div>
              <a
                href="#contact"
                className="btn-sweep text-sm text-slate-500 hover:text-white border border-slate-200 hover:border-emerald-500 rounded-lg px-3 py-1.5"
              >
                Need something custom?
              </a>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {SERVICES.map(({ title, Icon, desc }) => (
                <a
                  key={title}
                  href="#contact"
                  onClick={() => setService(title)}
                  className="group flex flex-col rounded-3xl bg-white p-8 shadow-sm ring-1 ring-black/[0.04] hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="mb-7 h-14 w-14 rounded-2xl bg-slate-50 ring-1 ring-black/[0.06] shadow-sm grid place-items-center text-emerald-600">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold tracking-tight text-slate-900 mb-3">{title}</h3>
                  <p className="text-[15px] text-slate-500 leading-relaxed flex-1">{desc}</p>
                  <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
                      Get started
                      <span className="inline-flex items-center group-hover:translate-x-1 transition-transform duration-200">
                        <IconArrow />
                      </span>
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Process ────────────────────────────────────────────────────────── */}
      <section className="bg-slate-950 text-white py-24">
        <div ref={procRef} className={`mx-auto max-w-6xl px-6 ${fc(procVisible)}`}>
          <div className="mb-14 text-center">
            <p className="text-xs font-semibold tracking-[0.2em] text-emerald-400 uppercase mb-2">How it works</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Our process</h2>
          </div>
          <div className="relative grid md:grid-cols-3 gap-8 md:gap-12">
            <div className="hidden md:block absolute left-0 right-0 h-px bg-slate-800" style={{ top: '11px' }} />
            {PROCESS.map(({ step, title, desc }) => (
              <div key={step} className="relative">
                <div className="text-xs font-bold tracking-[0.25em] text-emerald-400 mb-5">{step}</div>
                <h3 className="text-xl font-semibold mb-3">{title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About ──────────────────────────────────────────────────────────── */}
      <section id="about" className="mx-auto max-w-6xl px-6 py-24">
        <div ref={aboutRef} className={`grid md:grid-cols-2 gap-14 items-center ${fc(aboutVisible)}`}>
          <div className="order-2 md:order-1">
            <p className="text-xs font-semibold tracking-[0.2em] text-emerald-600 uppercase mb-2">Who we are</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">About Peronyx</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Peronyx is a small, focused dev and security shop. We build full-stack web applications and mobile apps, and help clients understand and improve their security posture. No bloated agencies, no hand-offs to junior devs — you work directly with the people building your product.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                'Web and mobile development, from first commit to App Store',
                'CompTIA Security+ certified, focused on practical security',
                'Transparent scope, fair pricing, and direct communication',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700 text-sm">
                  <span className="text-emerald-600"><IconCheck /></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="order-1 md:order-2">
            <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-200">
              <img
                src="/about.webp"
                alt="Working at Peronyx"
                className="object-cover w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact ────────────────────────────────────────────────────────── */}
      <section id="contact" className="relative overflow-hidden bg-slate-950 text-white py-28">
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 w-64 md:w-[40rem] h-64 blur-3xl opacity-10 bg-emerald-500 rounded-full" />
        <div className="relative mx-auto max-w-xl px-6">
          <div ref={contactRef} className={fc(contactVisible)}>
            <div className="text-center mb-12">
              <p className="text-xs font-semibold tracking-[0.28em] text-emerald-400 uppercase mb-4">Let's work together</p>
              <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight">
                Start a project.
              </h2>
              <p className="mt-4 text-slate-400 text-lg">
                Tell us what you're building. We'll take it from there.
              </p>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <input type="checkbox" name="botcheck" className="hidden" aria-hidden="true" />
              <input type="hidden" name="service" value={service || 'Not specified'} />
              <div>
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
                  I'm interested in
                </span>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {[...SERVICES.map(s => s.title), 'Other / Custom'].map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setService(prev => prev === option ? '' : option)}
                      className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-all duration-200 ${
                        service === option
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'bg-white/[0.06] border-white/10 text-slate-400 hover:border-white/30 hover:text-slate-200'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Name</span>
                  <input
                    name="name"
                    required
                    placeholder="Your name"
                    className="mt-2 w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Email</span>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="you@company.com"
                    className="mt-2 w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Phone <span className="normal-case tracking-normal font-normal text-slate-600">(optional)</span></span>
                <input
                  name="phone"
                  type="tel"
                  autocomplete="tel"
                  placeholder="(555) 000-0000"
                  className="mt-2 w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Message</span>
                <textarea
                  name="message"
                  required
                  rows={5}
                  placeholder="Tell us about your project — what you're building, where you're stuck, or what you need secured."
                  className="mt-2 w-full rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all resize-none"
                />
              </label>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-sweep w-full rounded-xl border border-emerald-500 text-emerald-400 hover:text-white px-5 py-3.5 text-sm font-semibold active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
              >
                {isSubmitting ? 'Sending…' : 'Send Message'}
              </button>
              {error && <p className="text-xs text-red-400 text-center">{error}</p>}
            </form>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
              <a href="mailto:peronyxtech@gmail.com" className="hover:text-slate-300 transition-colors">
                peronyxtech@gmail.com
              </a>
              <span>·</span>
              <span>Mon – Fri, 8am – 6pm CT</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-800 bg-slate-950 text-slate-400">
        <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6 text-sm">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 512 512" className="h-8 w-8 text-slate-200" fill="none" stroke="currentColor" strokeWidth={28} strokeLinecap="round" strokeLinejoin="round">
              <g transform="translate(256,256)">
                <circle cx="0" cy="0" r="140" />
                <path d="M -50 105 L -50 -105" />
                <path d="M -50 -105 L 10 -105 A 60 60 0 0 1 10 15 L -50 15" />
              </g>
            </svg>
            <span className="font-medium text-slate-200">Peronyx</span>
            <span className="text-slate-700">·</span>
            <span className="text-slate-500">Build well, ship fast, stay secure.</span>
          </div>
          <span className="text-slate-600 text-xs">© 2026 Peronyx</span>
          <nav className="flex items-center gap-6">
            <a href="#services" className="hover:text-slate-200 transition-colors duration-150">Services</a>
            <a href="#contact"  className="hover:text-slate-200 transition-colors duration-150">Contact</a>
          </nav>
        </div>
      </footer>

      {/* ── Thank-you modal ─────────────────────────────────────────────────── */}
      {showThankYou && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 backdrop-blur-sm backdrop-enter">
          <div className="mx-4 max-w-sm w-full rounded-2xl bg-white p-8 shadow-2xl text-center border border-slate-200 modal-enter">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <IconCheckCircle />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Request submitted</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              Thanks for reaching out. We'll get back to you as soon as possible.
            </p>
            <button
              onClick={() => setShowThankYou(false)}
              className="mt-6 inline-flex items-center rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800 active:scale-[0.97] transition-all duration-150"
            >
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
